import { For, createSignal, onCleanup, onMount } from "solid-js";
import { Button } from "./Button";

export type RideMode = "solo" | "duo";

const OPTIONS: { value: RideMode; title: string; bullets: string[] }[] = [
  {
    value: "solo",
    title: "Mode solo",
    bullets: ["Plus d'autonomie sur votre batterie", "50 % de CO2 économisé"],
  },
  {
    value: "duo",
    title: "Mode duo",
    bullets: ["Démarrer plus facilement avec un passager", "La vitesse ne changera pas"],
  },
];

/**
 * Figma U1/U2 (115:14098 / 115:14130): solo / duo choice, opened from the "Mode duo" toggle (05b,
 * 06b). Display only: no Pony call (decision 14/10, duo is faked). Tapping an option only moves the
 * selection; "Confirmer" calls `onChoose`. The dimmed area above the sheet, or Escape, closes it.
 * The caller shows the duo stories (S4–S6) the first time duo is confirmed (`storySeen`).
 */
export function DuoChoiceSheet(props: {
  value: RideMode;
  onChoose: (mode: RideMode) => void;
  onClose: () => void;
}) {
  let sheet: HTMLDivElement | undefined;
  const [picked, setPicked] = createSignal<RideMode>(props.value);

  onMount(() => {
    sheet?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });

  return (
    <div class="fixed inset-0 z-50 flex flex-col" role="presentation">
      {/* Figma hotspot/dismiss: the whole dimmed area above the sheet. */}
      <button
        type="button"
        class="min-h-6 flex-1 bg-black/50"
        aria-label="Fermer"
        tabindex="-1"
        onClick={() => props.onClose()}
      />
      <div
        ref={(el) => (sheet = el)}
        class="relative -mt-px max-h-[calc(100dvh-24px)] shrink-0 overflow-y-auto rounded-t-[24px] bg-white px-[15px] pt-[10px] outline-none shadow-[0_-2px_12px_rgba(0,0,0,0.16)]"
        style={{ "padding-bottom": "max(16px, env(safe-area-inset-bottom))" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="duo-choice-title"
        tabindex="-1"
      >
        <div class="mx-auto h-1 w-10 rounded-[2px] bg-handle" />
        <h2
          id="duo-choice-title"
          class="mt-[18px] text-center text-2xl font-semibold leading-[1.25] text-violet"
        >
          Choisissez votre mode
        </h2>
        <img
          src="/img/stories/u-duo-choice.png"
          width="193"
          height="177"
          alt=""
          class="mx-auto mt-3 h-[177px] w-[193px] object-contain short:h-[120px]"
        />
        <div role="radiogroup" aria-label="Mode" class="mt-[17px] flex flex-col gap-3">
          <For each={OPTIONS}>
            {(o) => {
              const on = () => picked() === o.value;
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={on()}
                  class={`relative flex min-h-24 w-full items-center rounded-[12px] border py-[14px] pl-4 pr-[52px] text-left ${
                    on()
                      ? "border-violet bg-surface2 shadow-[inset_0_0_0_1px_#6d3e91]"
                      : "border-border bg-white"
                  }`}
                  onClick={() => setPicked(o.value)}
                >
                  <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo">
                    <img src="/icons/bike-white-36.svg" width="26" height="26" alt="" />
                  </span>
                  <span class="ml-[15px] min-w-0 flex-1">
                    <span class="block text-base font-medium leading-[1.25] text-strong">
                      {o.title}
                    </span>
                    <span class="mt-1 block max-w-[240px] text-xs leading-[1.33] text-text">
                      <For each={o.bullets}>{(b) => <span class="block">• {b}</span>}</For>
                    </span>
                  </span>
                  <span class="absolute right-4 top-1/2 h-[22px] w-[22px] -translate-y-1/2">
                    {on() ? (
                      <>
                        <img src="/icons/radio-on-ring.svg" width="22" height="22" alt="" />
                        <img
                          src="/icons/radio-on-dot.svg"
                          width="10"
                          height="10"
                          alt=""
                          class="absolute left-[6px] top-[6px]"
                        />
                      </>
                    ) : (
                      <img src="/icons/radio-off.svg" width="22" height="22" alt="" />
                    )}
                  </span>
                </button>
              );
            }}
          </For>
        </div>
        <Button class="mt-[72px] short:mt-6" onClick={() => props.onChoose(picked())}>
          Confirmer
        </Button>
      </div>
    </div>
  );
}
